import {Component, OnInit} from '@angular/core';
import {SelectModule} from "primeng/select";
import {InputTextModule} from "primeng/inputtext";
import {NgIf} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Button} from "primeng/button";
import {Ripple} from "primeng/ripple";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {DeviseService} from "../../../core/services/devise.service";
import {ToastService} from "../../../core/services/toast.service";
import {getUnpagedPageable} from "../../../core/models/page.model";
import {InstitutionService} from "../../../core/services/institution.service";
import {IInstitution} from "../../../core/models/institution.model";
import {ICurrency} from "../../../core/models/devise.model";
import {CashRegisterService} from "../../../core/services/cash-register.service";
import {CashRegisterStatus, CashRegisterType, ICashRegister} from "../../../core/models/cash-register.model";
import {AccountService} from "../../../core/services/account.service";
import {IAccount} from "../../../core/models/account.model";
import {InputNumberModule} from "primeng/inputnumber";

@Component({
    selector: 'app-cash-register-details',
    imports: [
        SelectModule,
        InputTextModule,
        ReactiveFormsModule,
        NgIf,
        Button,
        Ripple,
        InputNumberModule
    ],
    templateUrl: './cash-register-details.component.html',
    styleUrls: ['./cash-register-details.component.scss']
})
export class CashRegisterDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    code: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    type: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    mainCurrency: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    minBalance: new FormControl<number | null>(null),
    maxBalance: new FormControl<number | null>(null),
    institutionId: new FormControl<string>('', {validators: [Validators.required], nonNullable: true}),
    linkedAccountId: new FormControl<string | null>(null),
  });

  boundData: CustomDialogData<ICashRegister> | undefined;
  isLoading: boolean = false;
  instance: DynamicDialog | undefined;
  currencies: ICurrency[] = [];
  institutions: IInstitution[] = [];
  accounts: IAccount[] = [];
  cashRegister: ICashRegister = {} as ICashRegister;
  cashRegisterTypes: {label: string, value: string}[] = [];

  constructor(
    private readonly cashRegisterService: CashRegisterService,
    private readonly dialogService: DialogService,
    private readonly deviseService: DeviseService,
    private readonly accountService: AccountService,
    private readonly toastService: ToastService,
    private readonly ref: DynamicDialogRef,
    private readonly institutionService: InstitutionService
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.initializeCashRegisterTypes();
    this.getCurrencies();
    this.getInstitutions();
    this.getAccounts();
    this.boundData = this.instance?.data as CustomDialogData<ICashRegister>;

    if (this.boundData?.isView || this.boundData?.isEdit) {
      this.cashRegister = this.boundData.data as ICashRegister;

      this.formGroup.patchValue({
        name: this.cashRegister.name,
        code: this.cashRegister.code,
        type: this.cashRegister.type,
        mainCurrency: this.cashRegister.mainCurrency.code,
        minBalance: this.cashRegister.minBalance,
        maxBalance: this.cashRegister.maxBalance,
        institutionId: this.cashRegister.institutionId,
        linkedAccountId: this.cashRegister.linkedAccount?.id || null,
      });

      if (this.boundData.isView) {
        this.formGroup.disable();
      }
    }
  }

  initializeCashRegisterTypes() {
    this.cashRegisterTypes = [
      { label: CashRegisterType.MAIN, value: CashRegisterType.MAIN },
      { label: CashRegisterType.SECONDARY, value: CashRegisterType.SECONDARY },
      { label: CashRegisterType.PETTY_CASH, value: CashRegisterType.PETTY_CASH },
      { label: CashRegisterType.EXCHANGE, value: CashRegisterType.EXCHANGE }
    ];
  }

  getCurrencies() {
    const summary = 'Récupération des devises';
    this.deviseService.getAllDevises(getUnpagedPageable()).subscribe({
      next: (page) => this.currencies = page.content,
      error: (err) => this.toastService.showToast(summary, err.error, 'error')
    });
  }

  getInstitutions() {
    this.institutionService.getAllInstitutions(getUnpagedPageable()).subscribe({
      next: (page) => this.institutions = page.content,
      error: (err) => {
        this.toastService.showToast('Récupération des institutions', err.error, 'error');
      }
    });
  }

  getAccounts() {
    this.accountService.getAllAccounts(getUnpagedPageable()).subscribe({
      next: (page) => this.accounts = page.content,
      error: (err) => {
        this.toastService.showToast('Récupération des comptes', err.error, 'error');
      }
    });
  }

  onSubmit() {
    if (this.formGroup.invalid) {
      this.toastService.showToast('Formulaire invalide', 'Veuillez remplir tous les champs requis', 'warn');
      return;
    }

    this.isLoading = true;
    const formValue = this.formGroup.value;

    const selectedCurrency = this.currencies.find(c => c.code === formValue.mainCurrency);
    const selectedAccount = formValue.linkedAccountId
      ? this.accounts.find(a => a.id === formValue.linkedAccountId)
      : undefined;

    const cashRegisterData: ICashRegister = {
      ...this.cashRegister,
      name: formValue.name!,
      code: formValue.code!,
      type: formValue.type as CashRegisterType,
      mainCurrency: selectedCurrency!,
      minBalance: formValue.minBalance || undefined,
      maxBalance: formValue.maxBalance || undefined,
      institutionId: formValue.institutionId!,
      linkedAccount: selectedAccount,
      status: this.cashRegister.status || CashRegisterStatus.ACTIVE
    };

    const operation$ = this.cashRegister.id
      ? this.cashRegisterService.updateCashRegister(this.cashRegister.id, cashRegisterData)
      : this.cashRegisterService.createCashRegister(cashRegisterData);

    operation$.subscribe({
      next: () => {
        const message = this.cashRegister.id ? 'Caisse modifiée avec succès' : 'Caisse créée avec succès';
        this.toastService.showToast('Succès', message, 'success');
        this.isLoading = false;
        this.ref.close(true);
      },
      error: (err) => {
        this.toastService.showToast('Erreur', err.error, 'error');
        this.isLoading = false;
      }
    });
  }

  onCancel() {
    this.ref.close(false);
  }
}
